/*
The MIT License (MIT)
Copyright (c) 2014 Chris Wilson
Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:
The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.
THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
*/

// Based on https://github.com/cwilso/volume-meter/blob/master/volume-meter.js

import { useCallback, useEffect, useMemo, useState } from 'react';
import useInterval from 'utils/hooks/useInterval';
import useUserMedia from 'utils/hooks/useUserMedia';

const useStreamVolume = (deviceId: string, sampleFreq: number, skip?: boolean): number => {
  const mediaConfig = useMemo(() => ({ audio: { deviceId } }), [deviceId]);
  const { stream } = useUserMedia(mediaConfig, !deviceId || skip);
  const [volume, setVolume] = useState(0);
  const rawVolume = useMemo(() => ({ volume: 0 }), []);

  // Sample periodically from raw volume so as not to re-render on every
  // audio frame
  useInterval(() => {
    setVolume(rawVolume.volume);
  }, sampleFreq);

  const processAudio = useCallback(
    (event) => {
      let buf = event.inputBuffer.getChannelData(0);
      let bufLength = buf.length;
      let sum = 0;
      let x;

      // Do a root-mean-square on the samples: sum up the squares...
      for (let i = 0; i < bufLength; i++) {
        x = buf[i];
        sum += x * x;
      }

      // ... then take the square root of the sum.
      let rms = Math.sqrt(sum / bufLength);

      // Now smooth this out with the averaging factor applied
      // to the previous sample - take the max here because we
      // want "fast attack, slow release."
      rawVolume.volume = Math.max(rms, rawVolume.volume * 0.95);
    },
    [rawVolume]
  );

  useEffect(() => {
    if (!stream) {
      return;
    }
    let AudioContext =
      window.AudioContext || // Default
      window.webkitAudioContext || // Safari and old versions of Chrome
      false;
    if (!AudioContext) {
      return;
    }
    const audioContext = new AudioContext();
    const mediaStreamSource = audioContext.createMediaStreamSource(stream);
    const processor = audioContext.createScriptProcessor(512);

    mediaStreamSource.connect(processor);
    processor.connect(audioContext.destination);
    processor.addEventListener('audioprocess', processAudio);

    return () => {
      processor.removeEventListener('audioprocess', processAudio);
      processor.disconnect(audioContext.destination);
      mediaStreamSource.disconnect(processor);
    };
  }, [processAudio, rawVolume, stream]);

  // Volume is the RMS value of the sine wave which is equivalent to Amplitude / sqrt(2)
  // Since volume is directly related to wave amplitude we multiple by 1.4 ~ sqrt(2)
  // To get back amplitude from RMS.
  return volume * 1.4;
};

export default useStreamVolume;
